// VacTicketContextPanel.tsx
// Context panel content for the vac_tickets tab — rendered inside SQLTable's sidepanel shell.
// Shows 4 cards per OI receipt: OI line items, Schedule match, Disposal/Bottoms, Invoice (placeholder).
import './VacTicketContextPanel.css';
import React, { useState } from 'react';
import { ChevronsRight as ChevronsRightIcon, CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import type { VacTicketAggregated, VacTicketItem } from '../../contexts/tableDefinitions';
import { carriersMatch } from '../../helper_functions';

// ── Constants ─────────────────────────────────────────────────────────────────

const ticketTypeLabel = (type: string): string =>
    type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const OI_STATUS_COLOR: Record<string, string> = {
    APPROVED:  'vtcp-badge--approved',
    SUBMITTED: 'vtcp-badge--submitted',
    DISPUTED:  'vtcp-badge--disputed',
    CANCELLED: 'vtcp-badge--cancelled',
};

const fmt = (v: number | null, decimals = 2, suffix = '') =>
    v == null ? 'N/A' : `${v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;

const fmtCurrency = (v: number | null) =>
    v == null ? 'N/A' : v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// ── Small helpers ─────────────────────────────────────────────────────────────

const Row: React.FC<{ label: string; value: string | null | undefined; mono?: boolean; mismatch?: boolean }> = ({ label, value, mono, mismatch }) => (
    <div className="vtcp-row">
        <span className="vtcp-rowLabel">{label}</span>
        <span className={`vtcp-rowValue${mono ? ' vtcp-rowValue--mono' : ''}${mismatch ? ' vtcp-rowValue--mismatch' : ''}`}>
            {value ?? 'N/A'}
        </span>
    </div>
);

const SectionHeader: React.FC<{
    title: string;
    badge?: React.ReactNode;
    collapsed?: boolean;
    onToggle?: () => void;
}> = ({ title, badge, collapsed, onToggle }) => (
    <div
        className={`vtcp-sectionHeader${onToggle ? ' vtcp-sectionHeader--clickable' : ''}`}
        onClick={onToggle}
    >
        <div className="vtcp-sectionHeaderLeft">
            {onToggle && (
                collapsed
                    ? <ChevronRight size={12} className="vtcp-chevron" />
                    : <ChevronDown  size={12} className="vtcp-chevron" />
            )}
            <span className="vtcp-sectionTitle">{title}</span>
        </div>
        {badge}
    </div>
);

// ── Card: OI Receipt ─────────────────────────────────────────────────────────

const OIReceiptCard: React.FC<{ items: VacTicketItem[]; showFinancials: boolean }> = ({ items, showFinancials }) => {
    const [collapsed, setCollapsed] = useState(false);
    return (
    <div className="vtcp-card">
        <SectionHeader
            title="OI Receipt"
            badge={<span className="vtcp-countBadge">{items.length} line{items.length !== 1 ? 's' : ''}</span>}
            collapsed={collapsed}
            onToggle={() => setCollapsed(c => !c)}
        />
        {!collapsed && (
        <div className="vtcp-cardBody">
        {items.length === 0 ? (
            <div className="vtcp-empty">No OI line items</div>
        ) : (
            <div className="vtcp-lineList">
                {items.map((item) => {
                    const typeLabel = item.ticket_type ? ticketTypeLabel(item.ticket_type) : '—';
                    const badgeCls = `vtcp-badge ${OI_STATUS_COLOR[item.oi_status ?? ''] ?? 'vtcp-badge--default'}`;
                    return (
                        <div key={item.oi_line_item_number} className="vtcp-lineItem">
                            <div className="vtcp-lineItemHeader">
                                <span className="vtcp-lineItemType">{typeLabel}</span>
                                <span className={badgeCls}>{item.oi_status ?? '—'}</span>
                            </div>
                            {item.oi_work_description && (
                                <Row label="Description" value={item.oi_work_description} />
                            )}
                            {item.oi_hours != null && (
                                <Row label="Hours" value={`${item.oi_hours}h`} />
                            )}
                            {item.oi_volume != null && (
                                <Row
                                    label="Volume"
                                    value={`${item.oi_volume.toLocaleString()} ${item.oi_volume_uom ?? ''}`}
                                />
                            )}
                            {item.disposal_volume != null && item.ticket_type === 'disposal' && (
                                <Row label="Disposal Vol" value={fmt(item.disposal_volume, 2, ' bbl')} />
                            )}
                            {showFinancials && item.oi_rate != null && (
                                <Row label="Rate" value={fmtCurrency(item.oi_rate)} />
                            )}
                            {showFinancials && item.oi_line_total != null && (
                                <Row label="Line Total" value={fmtCurrency(item.oi_line_total)} />
                            )}
                            {item.oi_disposal_ticket && (
                                <Row label="Disp. Ticket" value={item.oi_disposal_ticket} mono />
                            )}
                            {item.oi_disposal_location && (
                                <Row label="Disp. Location" value={item.oi_disposal_location} />
                            )}
                            {item.oi_invoice_number && (
                                <Row label="Invoice #" value={item.oi_invoice_number} mono />
                            )}
                        </div>
                    );
                })}
            </div>
        )}
        </div>
        )}
    </div>
    );
};

// ── Card: Schedule ────────────────────────────────────────────────────────────

const ScheduleCard: React.FC<{ ticket: VacTicketAggregated }> = ({ ticket }) => {
    const [collapsed, setCollapsed] = useState(false);

    if (!ticket.rid) {
        return (
            <div className="vtcp-card vtcp-card--absent">
                <SectionHeader
                    title="Schedule / RID"
                    badge={<XCircle size={14} className="vtcp-iconAbsent" />}
                    collapsed={collapsed}
                    onToggle={() => setCollapsed(c => !c)}
                />
                {!collapsed && (
                    <div className="vtcp-cardBody">
                        <div className="vtcp-empty">No schedule match found for this OI ticket</div>
                    </div>
                )}
            </div>
        );
    }

    const carrierMismatch = ticket.schedule_carrier_name != null &&
        !carriersMatch(ticket.schedule_carrier_name, ticket.oi_carrier);
    const producerMismatch = ticket.schedule_producer_name != null &&
        ticket.schedule_producer_name !== ticket.oi_producer;
    const leaseMismatch = ticket.schedule_lease_name != null &&
        ticket.schedule_lease_name !== ticket.oi_lease;
    const hasMismatch = carrierMismatch || producerMismatch || leaseMismatch;

    return (
        <div className="vtcp-card">
            <SectionHeader
                title="Schedule / RID"
                badge={hasMismatch
                    ? <AlertCircle size={14} className="vtcp-iconWarning" />
                    : <CheckCircle2 size={14} className="vtcp-iconMatch" />
                }
                collapsed={collapsed}
                onToggle={() => setCollapsed(c => !c)}
            />
            {!collapsed && (
            <div className="vtcp-cardBody">
                <div className="vtcp-ridBadge">{ticket.rid}</div>
                <Row label="Load Date"    value={ticket.schedule_load_date} />
                <Row label="Status"       value={ticket.schedule_status} />
                <Row label="Type"         value={ticket.schedule_type_of_work} />
                <Row label="Truck Ticket" value={ticket.schedule_truck_ticket} mono />
                <Row label="Producer"     value={ticket.schedule_producer_name} mismatch={producerMismatch} />
                <Row label="Lease"        value={ticket.schedule_lease_name}    mismatch={leaseMismatch} />
                <Row label="Carrier"      value={ticket.schedule_carrier_name}  mismatch={carrierMismatch} />
                <Row label="Requested By" value={ticket.poc} />
                {hasMismatch && (
                    <div className="vtcp-mismatchNote">
                        One or more fields differ between the schedule and OI receipt
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

// ── Card: Disposal / Bottoms ─────────────────────────────────────────────────

const DisposalCard: React.FC<{ disposalItems: VacTicketItem[] }> = ({ disposalItems }) => {
    const [collapsed, setCollapsed] = useState(false);
    if (disposalItems.length === 0) return null;

    return (
        <div className="vtcp-card">
            <SectionHeader
                title="Disposal"
                badge={<span className="vtcp-countBadge">{disposalItems.length} line{disposalItems.length !== 1 ? 's' : ''}</span>}
                collapsed={collapsed}
                onToggle={() => setCollapsed(c => !c)}
            />
            {!collapsed && (
            <div className="vtcp-cardBody">
            <div className="vtcp-lineList">
                {disposalItems.map((item) => {
                    const hasMatch = item.bmt_id != null;
                    return (
                        <div
                            key={item.oi_line_item_number}
                            className={`vtcp-lineItem${hasMatch ? '' : ' vtcp-lineItem--unmatched'}`}
                        >
                            <div className="vtcp-lineItemHeader">
                                <span className="vtcp-lineItemType">
                                    {item.oi_disposal_location ?? 'Unknown Location'}
                                </span>
                                {hasMatch
                                    ? <CheckCircle2 size={13} className="vtcp-iconMatch" />
                                    : <span className="vtcp-noMatchTag">No DB Match</span>
                                }
                            </div>
                            <Row label="OI Ticket(s)" value={item.oi_disposal_ticket} mono />
                            <Row label="Volume" value={fmt(item.disposal_volume, 2, ' bbl')} />
                            <div className="vtcp-subDivider" />
                            {hasMatch ? (
                                <>
                                    <Row label="FT #"         value={item.bmt_ft_number ?? item.bmt_final_ticket} mono />
                                    <Row label="FT Date"      value={item.bmt_load_date} />
                                    <Row label="Facility"     value={item.bmt_facility} />
                                    <Row label="Operator"     value={item.bmt_operator} />
                                    <Row label="Well"         value={item.bmt_well} />
                                    <Row label="Fluid Svc"    value={item.bmt_fluid_service} />
                                    <Row label="Net BBL"      value={fmt(item.bmt_net_barrels, 2, ' bbl')} />
                                    <Row label="Quantity"     value={fmt(item.bmt_quantity, 2, ' bbl')} />
                                    <Row label="Oil %"        value={fmt(item.bmt_oil_pct, 2, '%')} />
                                    <Row label="Water %"      value={fmt(item.bmt_water_pct, 2, '%')} />
                                </>
                            ) : (
                                <div className="vtcp-naBlock">
                                    N/A — no matching bottoms record
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            </div>
            )}
        </div>
    );
};

// ── Card: Invoice (placeholder) ──────────────────────────────────────────────

const InvoiceCard: React.FC = () => {
    const [collapsed, setCollapsed] = useState(false);
    return (
        <div className="vtcp-card vtcp-card--placeholder">
            <SectionHeader
                title="Invoice"
                collapsed={collapsed}
                onToggle={() => setCollapsed(c => !c)}
            />
            {!collapsed && (
                <div className="vtcp-cardBody">
                    <div className="vtcp-placeholderBody">Coming soon — Phase 3</div>
                </div>
            )}
        </div>
    );
};

// ── Main panel ───────────────────────────────────────────────────────────────

interface VacTicketContextPanelProps {
    onClose: () => void;
    ticket: VacTicketAggregated;
    showFinancials: boolean;
}

const VacTicketContextPanel: React.FC<VacTicketContextPanelProps> = ({
    onClose, ticket, showFinancials,
}) => {
    const disposalItems = ticket.line_items.filter(i => i.ticket_type === 'disposal');

    return (
        <div className="vtcp-root">
            {/* Header */}
            <div className="vtcp-header">
                <button className="vtcp-closeBtn" onClick={onClose} title="Close panel">
                    <ChevronsRightIcon size={18} />
                </button>
                <div className="vtcp-headerMeta">
                    <span className="vtcp-ticketNum">{ticket.oi_ticket_number}</span>
                    {ticket.oi_service_date && (
                        <span className="vtcp-serviceDate">{ticket.oi_service_date}</span>
                    )}
                    {ticket.oi_carrier && (
                        <span className="vtcp-carrier">{ticket.oi_carrier}</span>
                    )}
                </div>
            </div>

            <div className="vtcp-divider" />

            {/* Summary strip */}
            <div className="vtcp-summary">
                {ticket.oi_producer && <span className="vtcp-summaryItem">{ticket.oi_producer}</span>}
                {ticket.oi_lease    && <span className="vtcp-summaryItem vtcp-summaryItem--lease">{ticket.oi_lease}</span>}
                {ticket.work_types.map(wt => (
                    <span key={wt} className={`vtcp-workTypePill vtcp-workTypePill--${wt}`}>
                        {ticketTypeLabel(wt)}
                    </span>
                ))}
            </div>

            <div className="vtcp-divider" />

            {/* Cards */}
            <div className="vtcp-cards">
                <ScheduleCard ticket={ticket} />
                <OIReceiptCard items={ticket.line_items} showFinancials={showFinancials} />
                
                {disposalItems.length > 0 && (
                    <DisposalCard disposalItems={disposalItems} />
                )}
                <InvoiceCard />
            </div>
        </div>
    );
};

export default VacTicketContextPanel;
